import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavMobile from "../Nav/NavMobile";
import NavWeb from "../Nav/NavWeb";
import Card from "./Card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../ui/pagination";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import ManageTeamCompany from "./CrearModGrupo";
import Loader from "../../Loader/Loader";
import { DownloadIcon } from "@radix-ui/react-icons";
import { getAllTeams } from "../../../provider/adm/Grupos/getAllTeams";
import ScrollListProfesores from "./ScrollListProfesores";
import ScrollListCompanies from "./ScrollListCompanies";
import { Toaster } from "sonner";
import { ExcelExporter } from "../../../Lib/ExcelExporter";

const GruposEmpresas = () => {
  const [profesorSelectedToFilter, setprofesorSelectedToFilter] =
    React.useState("");
  const [companySelectedToFilter, setCompanySelectedToFilter] =
    React.useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 9;
  const navigate = useNavigate();

  // Función para comparar nombres (igual que en ScrollListProfesores)
  const compareNames = (name1, name2) => {
    if (!name1 || !name2) return false;
    const normalize = (str) => str.trim().replace(/\s+/g, " ").toLowerCase();
    return normalize(name1) === normalize(name2);
  };

  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const data_fromAPI = await getAllTeams(true);
        setData(data_fromAPI);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [setLoading]);

  const handleRowClick = (row) => {
    localStorage.setItem("groupData", JSON.stringify(row));
    navigate("/admin/gruposvista/grupos/groupdetail");
  };

  // Filtrado de datos mejorado
  const filteredData = useMemo(() => {
    if (loading) return [];
    let filtered = [...data]; // Crear copia para evitar mutaciones

    // Filtro por búsqueda de texto
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const searchableFields = [
          item.name,
          item.email,
          item.id?.toString(),
          item.companyName,
          item.companyNIT,
          item.teacherDescription?.fullName,
        ];

        return searchableFields.some(
          (field) =>
            field &&
            typeof field === "string" &&
            field.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filtro por profesor mejorado con compareNames
    if (profesorSelectedToFilter) {
      // Si es un array (multiselect)
      if (Array.isArray(profesorSelectedToFilter)) {
        if (profesorSelectedToFilter.length > 0) {
          filtered = filtered.filter((item) => {
            const teacherName = item.teacherDescription?.fullName;
            return teacherName && profesorSelectedToFilter.some((selectedName) =>
              compareNames(selectedName, teacherName)
            );
          });
        }
      }
      // Si es un string (single select)
      else if (
        typeof profesorSelectedToFilter === "string" &&
        profesorSelectedToFilter.trim()
      ) {
        filtered = filtered.filter((item) => {
          const teacherName = item.teacherDescription?.fullName;
          return teacherName && compareNames(profesorSelectedToFilter, teacherName);
        });
      }
    }

    // Filtro por compañía
    if (companySelectedToFilter) {
      // Si es un array (multiselect)
      if (Array.isArray(companySelectedToFilter)) {
        if (companySelectedToFilter.length > 0) {
          filtered = filtered.filter((item) => {
            const companyName = item.companyName;
            return companyName && companySelectedToFilter.some((selectedName) =>
              compareNames(selectedName, companyName)
            );
          });
        }
      }
      // Si es un string (single select)
      else if (
        typeof companySelectedToFilter === "string" &&
        companySelectedToFilter.trim()
      ) {
        filtered = filtered.filter((item) => {
          const companyName = item.companyName;
          return companyName && compareNames(companySelectedToFilter, companyName);
        });
      }
    }

    return filtered;
  }, [data, searchTerm, profesorSelectedToFilter, companySelectedToFilter, loading]);

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, profesorSelectedToFilter, companySelectedToFilter]);

  // Paginación mejorada
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  // Función para cambiar de página mejorada
  const handlePageChange = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
    }
  };

  // Función para limpiar filtros mejorada
  const clearFilters = () => {
    setSearchTerm("");
    setprofesorSelectedToFilter(
      Array.isArray(profesorSelectedToFilter) ? [] : ""
    );
    setCompanySelectedToFilter(
      Array.isArray(companySelectedToFilter) ? [] : ""
    );
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 9;

    if (totalPages === 0) return pageNumbers;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(i);
              }}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      if (currentPage <= 5) {
        for (let i = 1; i <= 6; i++) {
          pageNumbers.push(
            <PaginationItem key={i}>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(i);
                }}
                isActive={currentPage === i}
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
        pageNumbers.push(<span key="dots-1">...</span>);
        pageNumbers.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(totalPages);
              }}
              isActive={currentPage === totalPages}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        );
      } else if (currentPage >= totalPages - 4) {
        pageNumbers.push(
          <PaginationItem key={1}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(1);
              }}
            >
              1
            </PaginationLink>
          </PaginationItem>
        );
        pageNumbers.push(<span key="dots-2">...</span>);
        for (let i = totalPages - 5; i <= totalPages; i++) {
          pageNumbers.push(
            <PaginationItem key={i}>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(i);
                }}
                isActive={currentPage === i}
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
      } else {
        pageNumbers.push(
          <PaginationItem key={1}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(1);
              }}
            >
              1
            </PaginationLink>
          </PaginationItem>
        );
        pageNumbers.push(<span key="dots-3">...</span>);
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pageNumbers.push(
            <PaginationItem key={i}>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(i);
                }}
                isActive={currentPage === i}
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
        pageNumbers.push(<span key="dots-4">...</span>);
        pageNumbers.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(totalPages);
              }}
              isActive={currentPage === totalPages}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        );
      }
    }
    return pageNumbers;
  };

  const handleExportToExcel = () => {
    const success = ExcelExporter.exportGrupos(filteredData);
    if (success) {
      console.log("Excel exportado exitosamente");
    } else {
      console.error("Error al exportar Excel");
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ overflowY: "hidden", height: "100vh" }}
    >
      {loading && <Loader />}
      <NavMobile />
      <NavWeb />

      <div className="flex-1 p-6" style={{ overflowY: "scroll" }}>
        <div className="bg-white rounded-lg flex justify-between items-center p-5">
          <h2 className="text-xl font-bold text-gray-900">Lista de grupos</h2>
          <DownloadIcon
            className="h-6 w-6 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={handleExportToExcel}
            title="Exportar a Excel"
          />
        </div>
        <div className="flex items-center py-4 justify-between flex-wrap gap-4">
          <ScrollListCompanies
              companySelectedToFilter={companySelectedToFilter}
              setCompanySelectedToFilter={setCompanySelectedToFilter}
              setLoading={setLoading}
              multiSelect={true}
            />
          <Input
            placeholder="Search by name"
            className="w-56"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <div className="flex gap-4 items-center">
            <ScrollListProfesores
              profesorSelectedToFilter={profesorSelectedToFilter}
              setprofesorSelectedToFilter={setprofesorSelectedToFilter}
              setLoading={setLoading}
              multiSelect={true}
            />

            

            <Button variant="ghost" onClick={clearFilters}>
              Clear filters
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button>Crear nuevo grupo +</Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full md:w-1/3 bg-gray-100 text-black min-h-screen p-6 overflow-y-auto"
              >
                <SheetHeader>
                  <SheetTitle className="text-xl font-bold mb-4">
                    Create or Modify Group
                  </SheetTitle>
                </SheetHeader>
                <ManageTeamCompany initialData={{}} context={"create"} />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Información de resultados */}
        <div className="mb-4 text-sm text-gray-600">
          Mostrando {currentItems.length} de {filteredData.length} grupos
          {filteredData.length !== data.length &&
            ` (filtrados de ${data.length} total)`}
        </div>

        {/* Sección de Cartas */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {currentItems.length > 0 ? (
            currentItems.map((item, index) => (
              <Card
                key={`${item.id}-${index}`}
                image={item.photo}
                name={item.name}
                companyName={item.companyName}
                nit={item.companyNIT}
                status={item.status}
                data={item}
                onClick={() => handleRowClick(item)}
              />
            ))
          ) : (
            <div className="col-span-3 text-center py-8 text-gray-500">
              No se encontraron grupos que coincidan con los filtros aplicados.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage - 1);
                  }}
                  disabled={currentPage === 1}
                />
              </PaginationItem>
              {renderPageNumbers()}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage + 1);
                  }}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
      <Toaster />
    </div>
  );
};

export default GruposEmpresas;
